import type { BillingSummary } from '@immoaktas/api-client';
import { FREE_UNITS, PRICE_TIERS, yearlyPriceCents } from '@immoaktas/shared';

/** One line of the graduated price (design/Abo table, design/Paywall breakdown). Prices only from project.config.json. */
export type TierLine = { from: number; to: number | null; cents: number; count: number; subtotal: number; free: boolean };

/** Graduated breakdown for n billable units: the free units first, then every tier (count 0 when not reached). */
export function tierLines(units: number): TierLine[] {
  const n = Math.max(0, Math.floor(units));
  const free: TierLine = { from: 1, to: FREE_UNITS, cents: 0, count: Math.min(n, FREE_UNITS), subtotal: 0, free: true };
  return [free, ...PRICE_TIERS.map((t) => {
    const upper = t.toUnit ?? Number.POSITIVE_INFINITY;
    const count = Math.max(0, Math.min(n, upper) - (t.fromUnit - 1));
    return { from: t.fromUnit, to: t.toUnit, cents: t.cents, count, subtotal: count * t.cents, free: false };
  })];
}

/** Average yearly price per unit (all units incl. the free one). */
export const averageCents = (units: number) => (units > 0 ? Math.round(yearlyPriceCents(units) / units) : 0);

/** "1 frei + 9 × 7,90 € + 40 × 6,40 €": texts from the caller, numbers from the tiers. */
export function breakdownText(units: number, fmt: { free: (count: number) => string; part: (count: number, price: string) => string; price: (cents: number) => string }) {
  return tierLines(units).filter((l) => l.count > 0).map((l) => (l.free ? fmt.free(l.count) : fmt.part(l.count, fmt.price(l.cents)))).join(' + ');
}

/** Active web (Stripe) subscription: its units change via /billing/units, payment and cancellation in the Stripe portal. */
export const webActive = (b: BillingSummary | undefined) => !!b?.stripe?.active;
/**
 * Active store subscription that renews: it is extended or cancelled in the store (app), and no web subscription may be
 * added on top. A cancelled store subscription still running does not block the web checkout (docs/BILLING.md).
 */
export const storeRenews = (b: BillingSummary | undefined) => !!b?.store?.active && !!b.store.willRenew;

/** Subscription bought in the App Store / Play Store (RevenueCat): managed there, not in Stripe. */
export const isStoreSubscription = (source: string | null | undefined) => source === 'revenuecat' || source === 'apple' || source === 'google';
/** Subscription bought on the web: changes go through the Stripe customer portal. */
export const isWebSubscription = (b: { source: string | null; plan: string } | undefined) => !!b && b.source === 'stripe' && b.plan === 'paid';
